'use client';

import React from 'react';
import {
  faUpRightAndDownLeftFromCenter,
  faLocationDot,
  faBuildingColumns,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { sentenceCase } from 'src/utils/sentenceCase/sentenceCase';
import ScheduleDayCircles from '../TopProvidersSection/ScheduleDayCircles';

interface TopLocationCardProps {
  location: Record<string, any>;
  codeSetLabel: string;
}

const formatLocationType = (type: string) =>
  type === 'center' ? 'Hospital' : sentenceCase(type?.replace(/_/g, ' '));

const TopLocationCard: React.FC<TopLocationCardProps> = ({
  location,
  codeSetLabel,
}) => {
  const claimsDisplay = location.claims
    ? location.claims === '<11'
      ? location.claims
      : Number(location.claims).toLocaleString()
    : '0';

  return (
    <div
      className="rounded-2xl bg-white shadow-sm flex flex-col h-full hover:shadow-lg hover:scale-[1.02] transition-all"
      data-testid={`top-location-card-${location.provider_id}`}
      role="region"
      aria-label={location.name}
    >
      <div className="px-4 pt-4 pb-3 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <p
            className="text-sm font-bold text-neutral-800 leading-snug"
            data-testid={`top-location-name-${location.provider_id}`}
          >
            {location.name || 'N/A'}
          </p>
          <span className="shrink-0 flex items-center justify-center w-6 h-6">
            <Icon
              icon={faUpRightAndDownLeftFromCenter}
              className="w-3 h-3 text-neutral-700"
            />
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-neutral-600">
          <span className="flex items-center gap-1">
            <Icon icon={faBuildingColumns} className="w-3 h-3" />
            {location.soc_type
              ? location.soc_type
              : formatLocationType(location.type)}
          </span>
          {location.city && (
            <span className="flex items-center gap-1">
              <Icon icon={faLocationDot} className="w-3 h-3" />
              {location.city}
              {location.state ? `, ${location.state}` : ''}
            </span>
          )}
        </div>
      </div>

      <div className="border-t border-neutral-300" />

      <div className="px-4 pt-3 pb-4 flex flex-col gap-2">
        <div>
          <p className="text-sm font-bold text-neutral-800">{codeSetLabel}</p>
          <p className="text-sm text-neutral-600">
            <span className="text-lg font-semibold text-sky-600">{claimsDisplay}</span> at this location
          </p>
          {location.percentage != null && (
            <p className="text-xs text-neutral-500">
              {location.percentage}% of total
            </p>
          )}
        </div>
        {location.schedule && (
          <ScheduleDayCircles
            schedule={location.schedule}
            data-testid={`top-location-schedule-${location.provider_id}`}
          />
        )}
      </div>
    </div>
  );
};

export default TopLocationCard;
